import React, { useEffect, useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { Package, Users, Shield, Clock, MapPin, Award, ArrowRight, Globe, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
interface AboutPageProps {
  onNavigate?: (page: string) => void;
}
// Hero slider images for About page
const heroImages = [{
  url: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
  alt: 'Modern logistics warehouse'
}, {
  url: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
  alt: 'Cargo airplane loading'
}, {
  url: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
  alt: 'Shipping containers at port'
}];
export function AboutPage({
  onNavigate
}: AboutPageProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  // Preload images and auto-advance slider
  useEffect(() => {
    // Preload images
    heroImages.forEach(image => {
      const img = new Image();
      img.src = image.url;
    });
    // Auto-advance slider
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);
  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % heroImages.length);
  const prevSlide = () => setCurrentSlide(prev => (prev - 1 + heroImages.length) % heroImages.length);
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  const stats = [{
    value: '10+',
    label: 'Years Experience'
  }, {
    value: '50K+',
    label: 'Packages Delivered'
  }, {
    value: '99.9%',
    label: 'Delivery Success'
  }, {
    value: '4',
    label: 'US Locations'
  }];
  const values = [{
    icon: Shield,
    title: 'Reliability',
    description: 'We deliver on our promises. Every package, every time.'
  }, {
    icon: Clock,
    title: 'Speed',
    description: '7-14 day delivery from any US location to Lagos.'
  }, {
    icon: Users,
    title: 'Customer First',
    description: 'Your satisfaction drives everything we do.'
  }, {
    icon: Award,
    title: 'Excellence',
    description: 'We maintain the highest standards in logistics.'
  }];
  const team = [{
    name: 'Adebayo Johnson',
    role: 'Founder & CEO',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
  }, {
    name: 'Sarah Mitchell',
    role: 'Operations Director',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
  }, {
    name: 'Michael Chen',
    role: 'Logistics Manager',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
  }];
  return <div className="flex flex-col bg-white" data-id="element-1348">
      {/* Hero Section with Full-Screen Image Slider */}
      <section className="relative min-h-[600px] lg:min-h-[700px] text-white overflow-hidden -mt-20 sm:-mt-[104px] pt-20 sm:pt-[104px]" data-id="element-1349">
        {/* Background Image Slider */}
        <div className="absolute inset-0" data-id="element-1350">
          {heroImages.map((image, index) => <div key={index} className={`absolute inset-0 transition-opacity duration-700 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`} data-id="element-1351">
              <img src={image.url} alt={image.alt} className="absolute inset-0 w-full h-full object-cover" loading={index === 0 ? 'eager' : 'lazy'} data-id="element-1352" />
            </div>)}
          {/* Enhanced dark overlay for better text readability */}
          <div className="absolute inset-0 bg-brand-900/60" data-id="element-1353"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-brand-900/80 via-brand-900/50 to-brand-900/40" data-id="element-1354"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-brand-900/70 via-transparent to-brand-900/40" data-id="element-1355"></div>
        </div>

        {/* Slider Navigation - Bottom */}
        <div className="absolute bottom-4 sm:bottom-10 left-1/2 -translate-x-1/2 z-20 flex items-center gap-4" data-id="element-1356">
          <button onClick={prevSlide} className="w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm border border-white/30 flex items-center justify-center text-white hover:bg-black/40 transition-colors" data-id="element-1357">
            <ChevronLeft className="w-5 h-5" data-id="element-1358" />
          </button>
          <div className="flex gap-2" data-id="element-1359">
            {heroImages.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`h-2 rounded-full transition-all duration-300 ${index === currentSlide ? 'bg-white w-8' : 'bg-white/50 w-2 hover:bg-white/70'}`} data-id="element-1360" />)}
          </div>
          <button onClick={nextSlide} className="w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm border border-white/30 flex items-center justify-center text-white hover:bg-black/40 transition-colors" data-id="element-1361">
            <ChevronRight className="w-5 h-5" data-id="element-1362" />
          </button>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-24 sm:pt-20 sm:pb-20 lg:pt-28 lg:pb-28" data-id="element-1363">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="max-w-3xl" data-id="element-1364">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-black/40 border border-white/30 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-1365">
              <Package className="w-4 h-4" data-id="element-1366" />
              About TaskRunner
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-tight" style={{
            textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.4), 0 0 40px rgba(0,0,0,0.3)'
          }} data-id="element-1367">
              <span className="text-white" data-id="element-1368">Connecting America to Nigeria,</span>{' '}
              <span className="text-brand-300" style={{
              textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.4)'
            }} data-id="element-1369">
                One Package at a Time
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-white leading-relaxed max-w-2xl" style={{
            textShadow: '0 1px 3px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)'
          }} data-id="element-1370">
              For over a decade, TaskRunner has been the trusted bridge between
              the United States and Nigeria, delivering packages with care,
              speed, and reliability.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-gray-100" data-id="element-1371">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1372">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1373">
            {stats.map((stat, index) => <motion.div key={stat.label} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="text-center" data-id="element-1374">
                <div className="text-3xl sm:text-4xl font-bold text-brand-600 mb-1" data-id="element-1375">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-500" data-id="element-1376">{stat.label}</div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 lg:py-24 bg-gray-50" data-id="element-1377">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1378">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-1379">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-1380">
              <span className="inline-block px-4 py-1.5 bg-brand/10 text-brand text-sm font-semibold rounded-full mb-6" data-id="element-1381">
                Our Story
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6" data-id="element-1382">
                Built by the Community, for the Community
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed" data-id="element-1383">
                <p data-id="element-1384">
                  TaskRunner was born from a simple observation: shipping
                  packages from the USA to Nigeria was unnecessarily
                  complicated, expensive, and unreliable. Our founder, having
                  experienced these frustrations firsthand, decided to create a
                  better solution.
                </p>
                <p data-id="element-1385">
                  Starting with a single drop-off location in New York in 2014,
                  we've grown to serve thousands of customers across four major
                  US cities. Our mission remains the same: make international
                  shipping accessible, affordable, and stress-free.
                </p>
                <p data-id="element-1386">
                  Today, we're proud to be the preferred shipping partner for
                  families sending care packages, businesses expanding their
                  reach, and everyone in between.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative" data-id="element-1387">
              <div className="rounded-2xl overflow-hidden shadow-2xl" data-id="element-1388">
                <img src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt="TaskRunner warehouse operations" className="w-full h-auto" data-id="element-1389" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 border border-gray-100" data-id="element-1390">
                <div className="flex items-center gap-3" data-id="element-1391">
                  <div className="w-12 h-12 bg-brand-50 rounded-lg flex items-center justify-center" data-id="element-1392">
                    <Globe className="w-6 h-6 text-brand-600" data-id="element-1393" />
                  </div>
                  <div data-id="element-1394">
                    <div className="text-2xl font-bold text-gray-900" data-id="element-1395">
                      USA → NG
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-1396">Dedicated Route</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 lg:py-24 bg-white" data-id="element-1397">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1398">
          <div className="text-center mb-14" data-id="element-1399">
            <span className="inline-block px-4 py-1.5 bg-secondary/10 text-secondary text-sm font-semibold rounded-full mb-4" data-id="element-1400">
              Our Values
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-id="element-1401">
              What Drives Us
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1402">
              These core values guide every decision we make and every package
              we deliver.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1403">
            {values.map((value, index) => <motion.div key={value.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-gray-50 rounded-2xl p-6 text-center hover:shadow-lg transition-shadow" data-id="element-1404">
                <div className="w-14 h-14 bg-brand-50 rounded-xl flex items-center justify-center mx-auto mb-4" data-id="element-1405">
                  <value.icon className="w-7 h-7 text-brand-600" data-id="element-1406" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1407">
                  {value.title}
                </h3>
                <p className="text-sm text-gray-600" data-id="element-1408">{value.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 lg:py-24 bg-gray-50" data-id="element-1409">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1410">
          <div className="text-center mb-14" data-id="element-1411">
            <span className="inline-block px-4 py-1.5 bg-brand/10 text-brand text-sm font-semibold rounded-full mb-4" data-id="element-1412">
              Our Team
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-id="element-1413">
              Meet the People Behind TaskRunner
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1414">
              A dedicated team committed to making your shipping experience
              seamless.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto" data-id="element-1415">
            {team.map((member, index) => <motion.div key={member.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="text-center" data-id="element-1416">
                <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-white shadow-lg" data-id="element-1417">
                  <img src={member.image} alt={member.name} className="w-full h-full object-cover" data-id="element-1418" />
                </div>
                <h3 className="text-lg font-bold text-gray-900" data-id="element-1419">
                  {member.name}
                </h3>
                <p className="text-sm text-gray-500" data-id="element-1420">{member.role}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-brand-600" data-id="element-1421">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1422">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-1423">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" data-id="element-1424">
              Ready to Ship with Us?
            </h2>
            <p className="text-xl text-brand-100 mb-8 max-w-2xl mx-auto" data-id="element-1425">
              Join thousands of satisfied customers who trust TaskRunner for
              their USA to Nigeria shipping needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1426">
              <Button variant="white" size="lg" onClick={() => onNavigate?.('shipping')} data-id="element-1427">
                Start Shipping Now
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-1428" />
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10" onClick={() => onNavigate?.('contact')} data-id="element-1429">
                Contact Us
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}